import * as React from 'react';
import {
  AlertTriangle,
  CalendarRange,
  ClipboardCheck,
  Clock,
  Truck,
  Users,
  type LucideIcon,
} from 'lucide-react';

import { cn } from '@/shared/lib/utils';
import { Card } from '@/shared/ui/card';

import type { OperationsPulse } from '../api';
import { DonutChart, type DonutDatum } from './donut-chart';

type Tone = 'primary' | 'secondary' | 'success' | 'warning' | 'danger';

const TONE_ICON: Record<Tone, string> = {
  primary: 'tw-bg-primary/10 tw-text-primary',
  secondary: 'tw-bg-secondary/10 tw-text-secondary',
  success: 'tw-bg-success/10 tw-text-success',
  warning: 'tw-bg-warning/10 tw-text-warning',
  danger: 'tw-bg-destructive/10 tw-text-destructive',
};

function PulseShell({
  label,
  icon: Icon,
  tone,
  children,
}: {
  label: string;
  icon: LucideIcon;
  tone: Tone;
  children: React.ReactNode;
}) {
  return (
    <Card className="tw-flex tw-min-h-[124px] tw-flex-col tw-gap-2.5 tw-p-4">
      <div className="tw-flex tw-items-center tw-justify-between">
        <span className="tw-text-[10.5px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-muted-foreground">
          {label}
        </span>
        <span
          className={cn(
            'tw-flex tw-h-7 tw-w-7 tw-items-center tw-justify-center tw-rounded-lg',
            TONE_ICON[tone],
          )}
          aria-hidden="true"
        >
          <Icon className="tw-h-4 tw-w-4" />
        </span>
      </div>
      {children}
    </Card>
  );
}

function bigClass(danger = false) {
  return cn(
    'tw-text-[26px] tw-font-bold tw-leading-none tw-tabular-nums',
    danger ? 'tw-text-destructive' : 'tw-text-foreground',
  );
}

/**
 * OpsPulseStrip — the live "today" snapshot for the command center. Six compact
 * tiles answering the dispatcher's morning questions: who's out, what's
 * happening, what's due, what's deployed, what's waiting, what's slipping.
 */
export function OpsPulseStrip({ pulse }: { pulse: OperationsPulse }) {
  const { byStatus } = pulse;
  const activityDonut: DonutDatum[] = [
    { label: 'In progress', value: byStatus.inProgress, color: '#f7b84b' },
    { label: 'Scheduled', value: byStatus.scheduled, color: '#299cdb' },
    { label: 'Pending review', value: byStatus.pendingReview, color: '#405189' },
    { label: 'Approved', value: byStatus.approved, color: '#0ab39c' },
    { label: 'Cancelled', value: byStatus.cancelled, color: '#94a3b8' },
  ];

  const staffPct =
    pulse.staffTotal > 0
      ? Math.round((pulse.staffDeployed / pulse.staffTotal) * 100)
      : 0;

  return (
    <div className="tw-grid tw-grid-cols-2 tw-gap-3.5 sm:tw-grid-cols-3 xl:tw-grid-cols-6">
      {/* Crews deployed */}
      <PulseShell label="Crews deployed" icon={Users} tone="primary">
        <span className={bigClass()}>
          {pulse.crewsActive}
          <span className="tw-ml-1 tw-text-sm tw-font-medium tw-text-muted-foreground">
            active
          </span>
        </span>
        <div className="tw-mt-auto">
          <div className="tw-mb-1 tw-flex tw-items-baseline tw-justify-between tw-text-[11.5px] tw-text-muted-foreground">
            <span className="tw-tabular-nums">
              <span className="tw-font-semibold tw-text-foreground">
                {pulse.staffDeployed}
              </span>
              {pulse.staffTotal > 0 ? ` / ${pulse.staffTotal}` : ''} staff
            </span>
            {pulse.staffTotal > 0 ? (
              <span className="tw-tabular-nums">{staffPct}%</span>
            ) : null}
          </div>
          <div className="tw-h-1.5 tw-overflow-hidden tw-rounded-full tw-bg-muted">
            <div
              className="tw-h-full tw-rounded-full tw-bg-success tw-transition-all"
              style={{ width: `${pulse.staffTotal > 0 ? staffPct : 0}%` }}
            />
          </div>
        </div>
      </PulseShell>

      {/* Activities today (mini donut) */}
      <PulseShell label="Activities today" icon={CalendarRange} tone="secondary">
        <div className="tw-mt-1 tw-flex tw-items-center tw-gap-3">
          <DonutChart
            data={activityDonut}
            size={58}
            thickness={9}
            hideLegend
            centerValue={String(pulse.activitiesToday)}
          />
          <div className="tw-flex tw-flex-col tw-gap-0.5 tw-text-[11px] tw-text-muted-foreground">
            <span>
              <b className="tw-tabular-nums" style={{ color: '#f7b84b' }}>
                {byStatus.inProgress}
              </b>{' '}
              in progress
            </span>
            <span>
              <b className="tw-tabular-nums" style={{ color: '#299cdb' }}>
                {byStatus.scheduled}
              </b>{' '}
              scheduled
            </span>
            <span>
              <b className="tw-tabular-nums tw-text-success">
                {byStatus.approved + byStatus.pendingReview}
              </b>{' '}
              done / review
            </span>
          </div>
        </div>
      </PulseShell>

      {/* Tasks due today */}
      <PulseShell label="Tasks due today" icon={ClipboardCheck} tone="primary">
        <span className={bigClass()}>{pulse.tasksDue}</span>
        <span className="tw-mt-auto tw-text-[11.5px] tw-text-muted-foreground">
          across today&rsquo;s activities
        </span>
      </PulseShell>

      {/* Assets deployed */}
      <PulseShell label="Assets deployed" icon={Truck} tone="success">
        <span className={bigClass()}>
          {pulse.assetsDeployed}
          {pulse.assetsTotal > 0 ? (
            <span className="tw-ml-1 tw-text-sm tw-font-medium tw-text-muted-foreground">
              / {pulse.assetsTotal}
            </span>
          ) : null}
        </span>
        <span className="tw-mt-auto tw-text-[11.5px] tw-text-muted-foreground">
          {pulse.assetsTotal > 0
            ? `${Math.round((pulse.assetsDeployed / pulse.assetsTotal) * 100)}% of fleet in use`
            : 'vehicles & plant in use'}
        </span>
      </PulseShell>

      {/* Unassigned backlog */}
      <PulseShell label="Unassigned backlog" icon={Clock} tone="warning">
        <span className={bigClass()}>{pulse.unassignedBacklog}</span>
        <span className="tw-mt-auto tw-text-[11.5px] tw-text-muted-foreground">
          tasks not yet on the board
        </span>
      </PulseShell>

      {/* At risk */}
      <PulseShell label="At risk" icon={AlertTriangle} tone="danger">
        <span className={bigClass(pulse.atRisk > 0)}>{pulse.atRisk}</span>
        <span className="tw-mt-auto tw-text-[11.5px] tw-text-muted-foreground">
          in progress past planned end
        </span>
      </PulseShell>
    </div>
  );
}
